import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { format } from 'date-fns';

const InterviewDetailsModal = ({ isOpen, onClose, interview, onReschedule, onSendReminder }) => {
  if (!isOpen || !interview) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'pending':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'completed':
        return 'bg-success/10 text-success border-success/20';
      case 'rescheduled':
        return 'bg-accent/10 text-accent border-accent/20';
      default:
        return 'bg-muted/10 text-muted-foreground border-muted/20';
    }
  };

  const handleCopyMeetingLink = () => {
    if (interview?.meetingLink) {
      navigator?.clipboard?.writeText(interview?.meetingLink);
      alert('Meeting link copied to clipboard!');
    }
  };

  const handleJoinMeeting = () => {
    if (interview?.meetingLink) {
      window?.open(interview?.meetingLink, '_blank');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card border border-border rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-foreground">Interview Details</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Candidate Info */}
          <div className="flex items-start gap-4">
            <img
              src={interview?.candidatePhoto}
              alt={interview?.candidatePhotoAlt}
              className="w-20 h-20 rounded-full object-cover"
            />
            <div className="flex-1">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-foreground">{interview?.candidateName}</h3>
                  <p className="text-muted-foreground">{interview?.position}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(interview?.status)}`}>
                  {interview?.status?.charAt(0)?.toUpperCase() + interview?.status?.slice(1)}
                </span>
              </div>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Mail" size={16} />
                  <span>{interview?.email}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Phone" size={16} />
                  <span>{interview?.phone}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Briefcase" size={16} />
                  <span>{interview?.experience} experience</span>
                </div>
              </div>
            </div>
          </div>

          {/* Skills */}
          <div>
            <h4 className="text-sm font-medium text-foreground mb-2">Skills</h4>
            <div className="flex flex-wrap gap-2">
              {interview?.skills?.map((skill, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>

          {/* Interview Schedule */}
          {interview?.scheduledDate && (
            <div className="p-4 bg-muted/50 rounded-lg space-y-3">
              <h4 className="text-sm font-medium text-foreground">Interview Schedule</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex items-center gap-2 text-sm">
                  <Icon name="Calendar" size={16} className="text-muted-foreground" />
                  <span className="text-foreground">
                    {format(new Date(interview?.scheduledDate), 'MMMM dd, yyyy')}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Icon name="Clock" size={16} className="text-muted-foreground" />
                  <span className="text-foreground">
                    {format(new Date(interview?.scheduledDate), 'hh:mm a')}
                  </span>
                </div>
                {interview?.meetingPlatform && (
                  <div className="flex items-center gap-2 text-sm">
                    <Icon name="Video" size={16} className="text-muted-foreground" />
                    <span className="text-foreground">{interview?.meetingPlatform}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Meeting Link */}
          {interview?.meetingLink && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-foreground">Video Meeting Link</h4>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={interview?.meetingLink}
                  readOnly
                  className="flex-1 px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyMeetingLink}
                  iconName="Copy"
                >
                  Copy
                </Button>
                <Button
                  size="sm"
                  onClick={handleJoinMeeting}
                  iconName="ExternalLink"
                >
                  Join
                </Button>
              </div>
            </div>
          )}

          {/* Application Date */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="Calendar" size={16} />
            <span>Applied on {format(new Date(interview?.appliedDate), 'MMMM dd, yyyy')}</span>
          </div>

          {/* Preferred Times (for pending interviews) */}
          {interview?.status === 'pending' && interview?.preferredDates && (
            <div>
              <h4 className="text-sm font-medium text-foreground mb-2">Candidate's Preferred Dates</h4>
              <div className="flex flex-wrap gap-2">
                {interview?.preferredDates?.map((date, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-accent/10 text-accent rounded-lg text-sm"
                  >
                    {format(new Date(date), 'MMM dd, yyyy')}
                  </span>
                ))}
              </div>
              {interview?.preferredTime && (
                <p className="text-sm text-muted-foreground mt-2">
                  Preferred time: {interview?.preferredTime}
                </p>
              )}
            </div>
          )}

          {/* Rescheduled Info */}
          {interview?.status === 'rescheduled' && interview?.originalDate && (
            <div className="p-4 bg-warning/10 border border-warning/20 rounded-lg">
              <div className="flex items-start gap-2">
                <Icon name="AlertCircle" size={16} className="text-warning mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-warning">Interview Rescheduled</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Original date: {format(new Date(interview?.originalDate), 'MMM dd, yyyy hh:mm a')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            {interview?.status === 'scheduled' && (
              <>
                <Button
                  variant="outline"
                  onClick={() => onSendReminder?.(interview)}
                  iconName="Bell"
                >
                  Send Reminder
                </Button>
                <Button
                  variant="outline"
                  onClick={() => onReschedule?.(interview)}
                  iconName="RefreshCw"
                >
                  Reschedule
                </Button>
              </>
            )}
            {interview?.status === 'pending' && (
              <Button
                onClick={() => onReschedule?.(interview)}
                iconName="Calendar"
              >
                Schedule Interview
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterviewDetailsModal;